import { useSelector } from 'react-redux';
import { selectRoles, selectUser } from '../../redux/selectors/commonSelectors.ts';
import { evaluateAccess } from '@qcdoc/mock-runtime/contracts/accessEvaluator.ts';

export type PackagingAccess = 'read' | 'write' | 'admin' | 'vendor';
type Capabilities = { read: boolean; write: boolean; admin: boolean };

const VENDOR_PACKAGING_ROLES = ['Vendor', 'VendorAdmin', 'Packager', 'SuperUser'];

export function hasPackagingAccess(roles: string[], portal: string | undefined, access: PackagingAccess, capabilities?: Capabilities) {
  if (access === 'vendor') return roles.some(role => VENDOR_PACKAGING_ROLES.includes(role)) && (!portal || portal.toLowerCase() === 'qcdoc-vendor');
  const internal = portal ? portal.toLowerCase() === 'qcdoc' : !roles.some(role => ['Vendor', 'VendorAdmin', 'SuperUser', 'Packager'].includes(role));
  return internal && capabilities?.[access] === true;
}

export function usePackagingPermission() {
  const roles = useSelector(selectRoles);
  const user = useSelector(selectUser);
  const portal = user?.portal;
  const capabilities = user?.packaging;
  const profile = user?.accessProfile;
  const has = (permissionKey: 'Packaging.View' | 'Packaging.Create' | 'Packaging.ManageAssignments') => {
    if (!profile) return false;
    return profile.psls.filter((psl) => psl.isActive && !psl.isGlobal)
      .some((psl) => evaluateAccess(profile, permissionKey, psl.pslId).allowed)
      || profile.psls.some((psl) => psl.isActive && psl.isGlobal && evaluateAccess(profile, permissionKey, psl.pslId).allowed);
  };
  return {
    canReadPackaging: profile ? has('Packaging.View') : hasPackagingAccess(roles, portal, 'read', capabilities),
    canWritePackaging: profile ? has('Packaging.Create') : hasPackagingAccess(roles, portal, 'write', capabilities),
    canAdministerPackaging: profile ? has('Packaging.ManageAssignments') : hasPackagingAccess(roles, portal, 'admin', capabilities),
    canUseVendorPackaging: hasPackagingAccess(roles, portal, 'vendor'),
  };
}